import React from 'react'
import { FormProvider } from 'react-hook-form'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { capitalize } from 'transfix-ui/core'
import { INLINE_MESSAGE_VARIANTS, InlineMessage, useGlobalToast } from 'transfix-ui'
import { useCarrierStateQuery } from '../../Cards/CarrierStatus/graphql/CarrierState'
import { useCreateMotorCarrierInsuranceContactsMutation } from '../graphql/CreateMotorCarrierInsuranceContactsMutation'
import { AddInsuranceContactInformationModalForm } from './AddInsuranceContactInformationModalForm'
import { TAddInsuranceContactInformationModalSchema } from './AddInsuranceContactInformationModalSchema'
import { useAddInsuranceContactInformationModalForm } from './useAddInsuranceContactInformationModalForm'
import { parseData } from './utils'

interface IAddInsuranceContactInformationModalProps extends Omit<IModalBaseProps, 'modalType'> {
  carrierApplicationId: number
  refetch: () => void
}

export const AddInsuranceContactInformationModal = ({
  carrierApplicationId,
  onClose,
  open = false,
  refetch,
  ...props
}: IAddInsuranceContactInformationModalProps) => {
  const methods = useAddInsuranceContactInformationModalForm({
    defaultValues: {},
  })

  const { handleSubmit, reset, setValue } = methods || {}
  const { openToast } = useGlobalToast()

  const [createInsuranceContact, { error }] = useCreateMotorCarrierInsuranceContactsMutation()

  const { refetch: stateRefetch } =
    useCarrierStateQuery({ variables: { carrierApplicationId: `${carrierApplicationId}` } }) || {}

  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: () => {
        onClose()
      },
      type: 'dismiss',
      dataTest: 'cancel-button',
    },
    {
      label: 'Submit',
      action: handleSubmit(async (data: TAddInsuranceContactInformationModalSchema) => {
        const { data: response, errors } = await createInsuranceContact({
          variables: parseData(data),
        })
        const { createMotorCarrierInsuranceContacts } = response || {}
        const { errors: responseErrors } = createMotorCarrierInsuranceContacts || {}

        refetch()
        stateRefetch()
        if (errors?.length || responseErrors?.length) {
          const [firstError] = errors || responseErrors || []
          const { message } = firstError || {}
          return openToast({
            toastMessage: message ? capitalize(message.replace('_', ' ')) : 'Something went wrong',
          })
        }
        setValue('fullName', '')
        setValue('email', '')
        setValue('phoneNumber', '')
        setValue('organizationName', '')
        setValue('insuranceDocuments', '')
        reset()
        openToast({
          toastMessage: 'Insurance Contact Information Added',
        })
        return onClose()
      }),
      type: 'confirm',
      dataTest: 'confirm-button',
    },
  ]
  return (
    <ModalBase
      dataTest='AddInsuranceContactInformationModal'
      modalType={MODAL_TYPES.form}
      size={8}
      open={open}
      actions={actions}
      onClose={onClose}
      title='Submit Insurance Information'
      {...props}>
      <FormProvider {...methods}>
        {error && <InlineMessage message={error.message} type={INLINE_MESSAGE_VARIANTS.ALERT} />}
        <AddInsuranceContactInformationModalForm carrierApplicationId={`${carrierApplicationId}`} />
      </FormProvider>
    </ModalBase>
  )
}
